/**
 * face_catalog — the FACES a planet or a moon may wear (natural objects, 2026-10-08: "more textures for planets and
 * moons, to add variety"). PURE: no three, no DOM.
 *
 * The sector had five pooled faces (space_sector.WORLD_PLANET_POOL: desert, gas, ice, moon, ocean) and every kind wore
 * ONE of them, so two lava worlds — or two rocky moons — in different systems were the same picture under a tint. This
 * file lists the faces stored under textures/planets/faces/ (made by tools/asset-pipeline/gen-planet-faces.mjs: public-
 * domain USGS mosaics and procedural maps evaluated on the unit sphere; face_catalog_data.ts is that tool's table) and
 * says WHICH faces suit each planet kind and each moon kind. world_look.planetKindSpec / moonKindSpec draw one by the
 * body's own seed through mix01 (THE HASH LAW) — this file only holds the lists and picks by a number in [0, 1).
 *
 * LAWS
 *  - THE POOLED FACE STAYS IN THE DRAW. Every list begins with POOL: the kind's own pooled face is one of the choices,
 *    and it is what a body wears whenever its catalog face does not load (THE FALLBACK: a face is a picture, never a
 *    dependency — the sector does not wait on it and does not break over it).
 *  - A FACE SUITS A KIND BY WHAT THE KIND DOES TO IT. A kind's surface terms were written for a class of picture: the
 *    glint finds blue, dark texels; a drowned world keeps only what is bright and colourless (cloud, ice); a flattened
 *    one keeps the face's light-and-dark and none of its colour. So an ocean kind only wears ocean faces, an airless one
 *    only grey rock, a giant only cloud tops. `cls` is that class.
 *  - A FLATTENED KIND NEEDS A FACE WITH STRUCTURE (the worn sheet, 2026-10-08). A kind that keeps only a fraction of its
 *    face's light-and-dark (a greenhouse deck 0.13, a super-puff 0.25, a hazy moon 0.2, a sub-Neptune 0.5) draws a SMOOTH
 *    face as a plain disc — three such faces were one picture. Their lists name faces with strong structure (storm
 *    systems, belts) and at most one smooth one (a featureless deck is a real look).
 *  - THE MEAN TRAVELS WITH THE FACE. `flatten.mean` (the luminance a kind measures a face's detail against) is the
 *    chosen face's own `lum`, measured over the sphere by the tool; the pooled face's mean rides along (`poolMean`) and
 *    THE FALLBACK puts it back (`PlanetLook.wearPooled`), so a face that did not load is the old picture exactly.
 *  - `localStorage atlas:planet-faces = 'off'` (read by the views, next sector entry) = the five pooled faces alone.
 */
import type { PlanetKind, MoonKind } from './natural_types.js';
import { FACE_ROWS, type FaceRow } from './face_catalog_data.js';

export type { FaceRow };
/** The kind's own pooled face (space_sector.WORLD_PLANET_POOL[kind.face]). */
export const POOL = 'pool';
export const FACE_STORE_PREFIX = 'textures/planets/faces/';

const BY_NAME: ReadonlyMap<string, FaceRow> = new Map(FACE_ROWS.map((r) => [r.name, r]));
/** A stored face by name (undefined for POOL or a name the catalog does not hold). */
export const faceByName = (name: string): FaceRow | undefined => BY_NAME.get(name);
export const allFaces = (): ReadonlyArray<FaceRow> => FACE_ROWS;

/** What each PLANET kind may wear (4096 × 2048 faces). */
export const PLANET_FACES: Readonly<Record<PlanetKind, ReadonlyArray<string>>> = {
  airless: [POOL, 'rock_highlands', 'rock_maria', 'rock_saturated', 'rock_callisto', 'rock_mercury'],
  desert: [POOL, 'desert_ochre', 'desert_basalt', 'desert_dunes', 'desert_salt', 'desert_mars', 'desert_venus', 'rock_dusty'],
  temperate: [POOL, 'ocean_continents', 'ocean_archipelago', 'ocean_pangea', 'ocean_cold', 'ocean_storm'],
  water: [POOL, 'ocean_continents', 'ocean_archipelago', 'ocean_pangea', 'ocean_cold', 'ocean_storm'],
  greenhouse: [POOL, 'ocean_storm', 'ocean_cold', 'ocean_continents', 'cloud_veiled'],
  frozen: [POOL, 'ice_shell', 'ice_leads', 'ice_stained', 'ice_europa', 'ice_ganymede', 'ice_enceladus'],
  lava: [POOL, 'crust_plates', 'crust_ash', 'crust_shattered', 'crust_venus'],
  sub_neptune: [POOL, 'cloud_veiled', 'gas_pastel', 'gas_fine', 'gas_turbulent'],
  gas_giant: [POOL, 'gas_belts', 'gas_fine', 'gas_broad', 'gas_turbulent', 'gas_pastel', 'gas_barges'],
  hot_giant: [POOL, 'gas_turbulent', 'gas_broad', 'gas_barges', 'cloud_mottled'],
  ultra_hot_giant: [POOL, 'gas_turbulent', 'gas_belts', 'gas_broad', 'cloud_mottled'],
  ice_giant: [POOL, 'gas_smooth', 'gas_streaked', 'gas_fine', 'gas_broad'],
  super_puff: [POOL, 'gas_broad', 'gas_barges', 'gas_turbulent', 'cloud_chevron'],
};
/** What each MOON kind may wear (2048 × 1024 copies); a ring's shepherd wears its ring's pale dust, so it has its own list. */
export const MOON_FACES: Readonly<Record<MoonKind | 'shepherd', ReadonlyArray<string>>> = {
  rocky: [POOL, 'rock_highlands', 'rock_maria', 'rock_saturated', 'rock_dusty', 'rock_twotone', 'rock_callisto', 'rock_rhea', 'rock_dione', 'rock_tethys'],
  volcanic: [POOL, 'sulphur_fields', 'sulphur_pale', 'sulphur_scorched', 'sulphur_io'],
  icy_ocean: [POOL, 'ice_shell', 'ice_stained', 'ice_europa', 'ice_ganymede'],
  plume: [POOL, 'ice_bright', 'ice_enceladus', 'ice_shell'],
  hazy: [POOL, 'gas_belts', 'gas_broad', 'gas_turbulent', 'cloud_veiled'],
  irregular: [POOL, 'lump_grooved', 'lump_rubble', 'lump_dark'],
  shepherd: [POOL, 'lump_pale', 'ice_bright', 'rock_twotone'],
};

/** The class of picture each pooled index is (FACE: 0 desert, 1 gas, 2 ice, 3 moon, 4 ocean) — what a kind expects under its terms. */
export const POOL_CLASS: ReadonlyArray<ReadonlyArray<string>> = [
  ['desert', 'rock'],                 // desert: dust plains; a dusty cratered world passes for one
  ['gas', 'cloud'],                   // gas: cloud tops and hazes
  ['ice'],                            // ice
  ['rock', 'lump', 'crust', 'sulphur', 'ice'],   // moon: bare ground of any chemistry (a shepherd may be ice)
  ['ocean', 'cloud'],                 // ocean: seas under weather — or, flattened to a deck, the weather alone
];

/** `u` in [0, 1) → the entry of `list` it draws (uniform). */
const draw = (list: ReadonlyArray<string>, u: number): string => list[Math.min(list.length - 1, Math.max(0, Math.floor((Number.isFinite(u) ? u : 0) * list.length)))];
/**
 * The face a planet of `kind` wears for the draw `u` (the caller's mix01 of the planet's own seed): a stored face, or
 * null = its pooled face. A name the catalog does not hold (a face not yet stored) also answers null.
 */
export function planetFaceAt(kind: PlanetKind, u: number): FaceRow | null {
  const r = BY_NAME.get(draw(PLANET_FACES[kind] ?? [POOL], u));
  return r && r.planet ? r : null;
}
/** The face a moon of `kind` wears for the draw `u` (a shepherd from its own list): a stored face, or null = its pooled face. */
export function moonFaceAt(kind: MoonKind, shepherd: boolean, u: number): FaceRow | null {
  const r = BY_NAME.get(draw(MOON_FACES[shepherd ? 'shepherd' : kind] ?? [POOL], u));
  return r && r.moon ? r : null;
}
/** Store keys a PLANET loads for a face: its albedo, its normal map (none for cloud tops) and the normal's strength. */
export interface FaceTex { name: string; tex: string; normal?: string; bump: number }
export const planetFaceTex = (r: FaceRow): FaceTex => ({ name: r.name, tex: r.tex, ...(r.normal ? { normal: r.normal } : {}), bump: r.bump });
/** The store key a MOON loads for a face (the 2048 × 1024 copy). */
export const moonFaceKey = (r: FaceRow): string => r.small ?? r.tex;
